/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  Template as ServiceTemplate,
  TemplateScope,
  createTemplate,
  getTemplates,
  updateTemplate,
  deleteTemplate,
} from '../../services/templateService';
import { RootState } from '../../reducers';
import templates from './tabs/templates/templates';

// Extend the service template type to include optional properties from local templates
export interface Template
  extends Omit<ServiceTemplate, '_id' | 'teamId' | 'ownerId'> {
  featured?: boolean;
  intents?: string[];
  _id: string;
  teamId: string;
  ownerId: string;
  scope: TemplateScope;
}

// Type for creating a new template
export type NewTemplate = {
  id?: string;
  title: string;
  description?: string;
  prompt: string;
  scope?: TemplateScope;
  featured?: boolean;
  intents?: string[];
};

interface TemplatesState {
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  templates: Template[];
}

const initialState: TemplatesState = {
  status: 'idle',
  error: null,
  templates: [],
};

export const fetchTemplates = createAsyncThunk<
  Template[],
  TemplateScope | undefined
>('templates/fetchTemplates', async (scope) => {
  const response = await getTemplates(scope);
  return response || [];
});

export const addTemplate = createAsyncThunk<Template, NewTemplate>(
  'templates/addTemplate',
  async (template) => {
    const response = await createTemplate(template);
    return response.data;
  }
);

export const customizeBuiltInTemplate = createAsyncThunk<
  Template,
  { id: string; prompt: string; description?: string; scope?: TemplateScope }
>(
  'templates/customizeBuiltInTemplate',
  async ({ id, prompt, description, scope = 'personal' }) => {
    const builtInTemplate = templates.find((t: { id: string }) => t.id === id);
    if (!builtInTemplate) {
      throw new Error('Template not found');
    }

    // Use the new upsert logic in createTemplate - it will automatically handle existing templates
    const createResponse = await createTemplate({
      id: builtInTemplate.id, // Same ID as built-in template (override mechanism)
      title: builtInTemplate.title,
      prompt,
      description,
      scope, // Specify the scope (personal, team, or public)
    });

    return createResponse.data;
  }
);

export const editTemplate = createAsyncThunk<
  Template,
  { id: string; template: Partial<NewTemplate>; scope: TemplateScope }
>('templates/editTemplate', async ({ id, template, scope }) => {
  const response = await updateTemplate(id, template, scope);
  return response.data;
});

export const removeTemplate = createAsyncThunk<
  string,
  { id: string; scope: TemplateScope }
>('templates/removeTemplate', async ({ id, scope }) => {
  await deleteTemplate(id, scope);
  return id;
});

// Helper function to create a Template from a built-in template
export const createTemplateFromBuiltIn = (
  builtInTemplate: {
    id: string;
    title: string;
    description?: string;
    prompt: string;
    featured?: boolean;
    intents?: string[];
  },
  scope: TemplateScope = 'personal'
): Template => {
  return {
    _id: '',
    teamId: '',
    ownerId: '',
    scope,
    id: builtInTemplate.id, // Keep the original ID
    title: builtInTemplate.title,
    description: builtInTemplate.description || '',
    prompt: builtInTemplate.prompt,
    featured: builtInTemplate.featured,
    intents: builtInTemplate.intents,
  };
};

const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch templates
      .addCase(fetchTemplates.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchTemplates.fulfilled, (state, { payload }) => {
        state.status = 'succeeded';
        state.templates = payload;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      })
      // Add template
      .addCase(addTemplate.fulfilled, (state, { payload }) => {
        state.templates.push(payload);
      })
      // Customize built-in template
      .addCase(customizeBuiltInTemplate.fulfilled, (state, { payload }) => {
        const index = state.templates.findIndex(
          (template) => template.id === payload.id
        );
        if (index !== -1) {
          state.templates[index] = payload;
        } else {
          state.templates.push(payload);
        }
      })
      // Edit template
      .addCase(editTemplate.fulfilled, (state, { payload }) => {
        const index = state.templates.findIndex(
          (template) => template.id === payload.id
        );
        if (index !== -1) {
          state.templates[index] = payload;
        }
      })
      // Remove template
      .addCase(removeTemplate.fulfilled, (state, { payload }) => {
        state.templates = state.templates.filter(
          (template) => template.id !== payload
        );
      });
  },
});

export const selectAllTemplates = (state: RootState) =>
  state.templates?.templates || [];
export const selectTemplatesStatus = (state: RootState) =>
  state.templates?.status || 'idle';
export const selectTemplatesError = (state: RootState) =>
  state.templates?.error || null;

// Scope-based selectors
export const selectPersonalTemplates = (state: RootState) =>
  state.templates?.templates.filter((t) => t.scope === 'personal') || [];
export const selectTeamTemplates = (state: RootState) =>
  state.templates?.templates.filter((t) => t.scope === 'team') || [];
export const selectPublicTemplates = (state: RootState) =>
  state.templates?.templates.filter((t) => t.scope === 'public') || [];

// Helper selector for templates by scope
export const selectTemplatesByScope = (
  state: RootState,
  scope: TemplateScope
) => state.templates?.templates.filter((t) => t.scope === scope) || [];

export default templatesSlice.reducer;
